import React, { createContext, useState, ReactNode, useEffect } from 'react';
import { auth } from '../firebase';
import { User, onAuthStateChanged, signInWithEmailAndPassword, signOut, updatePassword, reauthenticateWithCredential, EmailAuthProvider, setPersistence, browserLocalPersistence, browserSessionPersistence } from 'firebase/auth';
import { getSupabaseToken, ensureSupabaseTokenFresh, clearSupabaseTokenCache } from '../lib/supabase';
import { unregisterFcmToken } from '../services/notifications/FCMService';

interface AuthContextType {
  authUser: User | null;
  loading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  updateUserPassword: (newPassword: string, currentPassword?: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
      setLoading(false);

      // Pre-warm the Supabase token in the background if user is logged in
      if (user) {
        getSupabaseToken().catch(err => console.warn("Background token pre-warm failed:", err));

        // Penyegaran proaktif berulang: token disegarkan DI LATAR BELAKANG
        // saat sisa masa berlakunya < 12 jam, dan dicek ulang setiap 6 jam.
        // Ini memastikan token SELALU segar ketika tombol presensi ditekan,
        // sehingga AUTH_TOKEN_MISSING tidak muncul di momen submit.
        ensureSupabaseTokenFresh().catch(err => console.warn("Background token refresh failed:", err));
        const refreshInterval = setInterval(() => {
          ensureSupabaseTokenFresh().catch(err => console.warn("Periodic token refresh failed:", err));
        }, 6 * 60 * 60 * 1000);
        return () => clearInterval(refreshInterval);
      }
    });
    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string, rememberMe: boolean = true): Promise<void> => {
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      await signInWithEmailAndPassword(auth, email, password);
      // Pre-warm token immediately after login succeeds
      getSupabaseToken().catch(err => console.warn("Background token pre-warm failed:", err));
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    // Perangkat ini tidak boleh lagi menerima notifikasi akun yang logout.
    await unregisterFcmToken();
    // Token Supabase tersimpan di localStorage → wajib dibuang saat logout
    // supaya tidak terbawa ke akun lain di HP yang sama.
    clearSupabaseTokenCache();
    await signOut(auth);
  };

  const updateUserPassword = async (newPassword: string, currentPassword?: string): Promise<void> => {
    if (!auth.currentUser) {
      throw new Error("Tidak ada pengguna yang sedang login.");
    }
    try {
      // If current password is provided, try to reauthenticate first
      if (currentPassword && auth.currentUser.email) {
          const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
          await reauthenticateWithCredential(auth.currentUser, credential);
      }

      await updatePassword(auth.currentUser, newPassword);
    } catch (error: any) {
      console.error("Error updating password:", error);
      if (error.code === 'auth/wrong-password') {
          throw new Error("Password lama yang Anda masukkan salah.");
      }
      if (error.code === 'auth/requires-recent-login') {
        throw new Error("Sesi Anda sudah terlalu lama. Silakan masukkan password lama Anda untuk verifikasi.");
      }
      if (error.code === 'auth/weak-password') {
        throw new Error("Password terlalu lemah. Harus terdiri dari minimal 6 karakter.");
      }
      throw new Error("Gagal mengubah password: " + error.message);
    }
  };

  const value = {
    authUser,
    loading,
    login,
    logout,
    updateUserPassword,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};
